import { gridOf, type Board, type GameState } from '@archon3d/core';
import { TABLE_YAW } from './constants';

/** A sixth of a turn: the angle between two sides of a hexagon. */
const SIXTH = Math.PI / 3;

/**
 * Which way the board faces on the table. A square board turns freely: the seat logic points the
 * player's home edge straight at them. A hex board is a hexagon whose sides must stay parallel
 * with the six-sided table's, so it may only turn in sixths of a turn from the table's own yaw;
 * the home edge is brought as near the player as that allows, which is exactly in front when the
 * side sits on one of the hexagon's sides. A side seated on a corner lies between two sides;
 * the tie goes to the yaw `Math.round` picks, the board turning the extra half-step clockwise.
 */
export function seatYawFor(board: Board, homeYaw: number | null): number {
  if (gridOf(board) !== 'hex') return homeYaw ?? 0;
  if (homeYaw === null) return TABLE_YAW;
  return TABLE_YAW + Math.round((homeYaw - TABLE_YAW) / SIXTH) * SIXTH;
}

/**
 * The side the table turns to face: the one person's; the one to move when several share it;
 * and `null`, the neutral side, for AI against AI. While an AI moves at a table of several people
 * it answers `undefined`, stay where it is: it swung to the neutral side and back every round,
 * hiding the AI's move behind the turn (client-board:bugs#8). Just set up (`settled` false) it
 * faces whichever person moves next.
 */
export function facingFaction(
  state: GameState,
  humans: readonly string[],
  settled: boolean,
): string | null | undefined {
  if (humans.length === 0) return null;
  if (humans.length === 1) return humans[0]!;
  if (humans.includes(state.activeFaction)) return state.activeFaction;
  if (settled) return undefined;
  const order = state.factions;
  const at = order.findIndex((f) => f.id === state.activeFaction);
  for (let i = 1; i <= order.length; i++) {
    const next = order[(at + i) % order.length]!;
    if (!next.eliminated && humans.includes(next.id)) return next.id;
  }
  return humans[0]!;
}
